import React, { useEffect, useRef, useState } from "react";
import redPin from "../../Images/red_pin.png"
import {BlueprintLocation} from "../../types/protocols";

interface RoomImageProps {
    imageUrl: string;
    location: BlueprintLocation;
}

const RoomImage: React.FC<RoomImageProps> = ({ imageUrl, location }) => {
    const DOT_SIZE = 26;
    const containerRef = useRef<HTMLDivElement>(null);
    const imageRef = useRef<HTMLImageElement>(null);

    const [imageDimensions, setImageDimensions] = useState<{
        width: number;
        height: number;
    } | null>(null);

    const handleImageLoad = () => {
        if (imageRef.current) {
            setImageDimensions({
                width: imageRef.current.naturalWidth,
                height: imageRef.current.naturalHeight,
            });
        }
    };

    const calculateDotPosition = () => {
        if (!imageRef.current || !containerRef.current || !imageDimensions) {
            return { left: 0, top: 0 };
        }

        const container = containerRef.current;
        const image = imageRef.current;

        // Get the actual displayed image dimensions
        const imageRect = image.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();

        // Calculate scale based on the blueprint dimensions vs actual image dimensions
        const scaleX = imageRect.width / location.width;
        const scaleY = imageRect.height / location.height;

        // Calculate the scaled coordinates
        const scaledX = location.x * scaleX;
        const scaledY = location.y * scaleY;

        // Calculate image offset within container
        const imageOffsetX = (containerRect.width - imageRect.width) / 2;
        const imageOffsetY = (containerRect.height - imageRect.height) / 2;

        return {
            left: imageOffsetX + scaledX,
            top: imageOffsetY + scaledY
        };
    };

    const [dotPosition, setDotPosition] = useState({ left: 0, top: 0 });

    useEffect(() => {
        const updateDotPosition = () => {
            setDotPosition(calculateDotPosition());
        };

        // Create ResizeObserver to watch for container size changes
        const resizeObserver = new ResizeObserver(updateDotPosition);

        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }

        // Update position initially and when image or location changes
        updateDotPosition();

        return () => {
            if (containerRef.current) {
                resizeObserver.unobserve(containerRef.current);
            }
        };
    }, [location, imageDimensions]);

    return (
        <div
            ref={containerRef}
            style={{
                position: "relative",
                width: "100%",
                height: "100%",
                overflow: "hidden",
            }}
        >
            <img
                ref={imageRef}
                src={imageUrl}
                alt="Room Plan"
                onLoad={handleImageLoad}
                style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                }}
            />
            {imageDimensions && (
                <img
                    src={redPin}
                    alt="Location Pin"
                    style={{
                        position: "absolute",
                        width: `${DOT_SIZE}px`,
                        height: `${DOT_SIZE}px`,
                        left: `${dotPosition.left}px`,
                        top: `${dotPosition.top - DOT_SIZE/2}px`,
                        transform: "translate(-50%, -50%)",
                        pointerEvents: "none",
                    }}
                />
            )}
        </div>
    );
};

export default RoomImage;